/**
 * 2D layout editors — slots cabinet plates + poker seat rails.
 * Used only from /admin (allowlisted operators).
 */
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type {
  LayoutBundle,
  SlotsLayoutV1,
  TableLayoutV1,
} from "@shared/admin-layouts";
import {
  DEFAULT_SLOTS_LAYOUT,
  DEFAULT_TABLE_LAYOUTS,
  sanitizeSlotsLayout,
  sanitizeTableLayout,
} from "@shared/admin-layouts";
import { Button } from "@/components/ui/button";
import { writeDraftBundle } from "@/lib/layout-runtime";

type SlotsRegionKey =
  | "marquee"
  | "reels"
  | "win"
  | "betCircle"
  | "linesCircle"
  | "spin"
  | "auto"
  | "info"
  | "lever";

const SLOT_REGIONS: {
  key: SlotsRegionKey;
  label: string;
  color: string;
}[] = [
  { key: "marquee", label: "Marquee", color: "#fbbf24" },
  { key: "reels", label: "Reels glass", color: "#22d3ee" },
  { key: "win", label: "Win strip", color: "#a78bfa" },
  { key: "betCircle", label: "Bet", color: "#4ade80" },
  { key: "linesCircle", label: "Lines", color: "#34d399" },
  { key: "spin", label: "Spin", color: "#f472b6" },
  { key: "auto", label: "Auto", color: "#fb923c" },
  { key: "info", label: "Info", color: "#60a5fa" },
  { key: "lever", label: "Lever", color: "#f87171" },
];

function regionRect(
  layout: SlotsLayoutV1,
  key: SlotsRegionKey,
): { left: number; top: number; width: number; height: number } {
  const L = layout;
  switch (key) {
    case "marquee":
      return {
        left: L.marquee.l,
        top: L.marquee.t,
        width: 100 - L.marquee.l - L.marquee.r,
        height: L.marquee.h,
      };
    case "reels":
      return {
        left: L.reels.l,
        top: L.reels.t,
        width: 100 - L.reels.l - L.reels.r,
        height: 100 - L.reels.t - L.reels.b,
      };
    case "win":
      return {
        left: L.win.l,
        top: L.win.t,
        width: 100 - L.win.l - L.win.r,
        height: L.win.h,
      };
    case "betCircle":
      return {
        left: L.betCircle.l,
        top: L.betCircle.t,
        width: L.betCircle.s,
        height: L.betCircle.s,
      };
    case "linesCircle":
      return {
        left: L.linesCircle.l,
        top: L.linesCircle.t,
        width: L.linesCircle.s,
        height: L.linesCircle.s,
      };
    case "spin":
      return {
        left: L.spin.l,
        top: L.spin.t,
        width: 100 - L.spin.l - L.spin.r,
        height: L.spin.h,
      };
    case "auto":
      return {
        left: L.auto.l,
        top: L.auto.t,
        width: L.auto.w,
        height: L.auto.h,
      };
    case "info":
      return {
        left: L.info.l,
        top: L.info.t,
        width: L.info.w,
        height: L.info.h,
      };
    case "lever":
      return {
        left: 100 - L.lever.r - L.lever.w,
        top: L.lever.t,
        width: L.lever.w,
        height: L.lever.h,
      };
  }
}

function applyRegionDrag(
  layout: SlotsLayoutV1,
  key: SlotsRegionKey,
  dLeft: number,
  dTop: number,
): SlotsLayoutV1 {
  const next = structuredClone(layout);
  const move = (obj: { l?: number; t?: number; r?: number }, dl: number, dt: number) => {
    if (obj.l != null) obj.l = Math.min(95, Math.max(0, obj.l + dl));
    if (obj.t != null) obj.t = Math.min(95, Math.max(0, obj.t + dt));
  };
  switch (key) {
    case "marquee":
      move(next.marquee, dLeft, dTop);
      break;
    case "reels":
      move(next.reels, dLeft, dTop);
      break;
    case "win":
      move(next.win, dLeft, dTop);
      break;
    case "betCircle":
      next.betCircle.l = Math.min(90, Math.max(0, next.betCircle.l + dLeft));
      next.betCircle.t = Math.min(95, Math.max(0, next.betCircle.t + dTop));
      break;
    case "linesCircle":
      next.linesCircle.l = Math.min(90, Math.max(0, next.linesCircle.l + dLeft));
      next.linesCircle.t = Math.min(95, Math.max(0, next.linesCircle.t + dTop));
      break;
    case "spin":
      move(next.spin, dLeft, dTop);
      break;
    case "auto":
      next.auto.l = Math.min(90, Math.max(0, next.auto.l + dLeft));
      next.auto.t = Math.min(95, Math.max(0, next.auto.t + dTop));
      break;
    case "info":
      next.info.l = Math.min(90, Math.max(0, next.info.l + dLeft));
      next.info.t = Math.min(95, Math.max(0, next.info.t + dTop));
      break;
    case "lever":
      // lever anchored from right
      next.lever.r = Math.min(40, Math.max(0, next.lever.r - dLeft));
      next.lever.t = Math.min(90, Math.max(0, next.lever.t + dTop));
      break;
  }
  return sanitizeSlotsLayout(next);
}

export function SlotsLayoutEditor({
  layout,
  onChange,
  cabinetSrc = "/slots/gen/cabinet-mesh.jpg",
}: {
  layout: SlotsLayoutV1;
  onChange: (l: SlotsLayoutV1) => void;
  cabinetSrc?: string;
}) {
  const boardRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<SlotsRegionKey>("reels");
  const drag = useRef<{
    key: SlotsRegionKey;
    x: number;
    y: number;
    layout: SlotsLayoutV1;
  } | null>(null);

  const onPointerDown = (key: SlotsRegionKey, e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(key);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = {
      key,
      x: e.clientX,
      y: e.clientY,
      layout,
    };
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current || !boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    const dLeft = ((e.clientX - drag.current.x) / rect.width) * 100;
    const dTop = ((e.clientY - drag.current.y) / rect.height) * 100;
    onChange(applyRegionDrag(drag.current.layout, drag.current.key, dLeft, dTop));
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const nudge = (dl: number, dt: number) => {
    onChange(applyRegionDrag(layout, selected, dl, dt));
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-400">
        Drag plates on the cabinet mesh. Selected region also responds to
        Nudge. Preview applies via local draft until you{" "}
        <strong className="text-amber-200">Publish</strong>.
      </p>
      <div className="flex flex-wrap gap-1">
        {SLOT_REGIONS.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${
              selected === r.key
                ? "border-amber-400 text-amber-200 bg-amber-950/40"
                : "border-zinc-700 text-zinc-400"
            }`}
            style={{ borderColor: selected === r.key ? r.color : undefined }}
            onClick={() => setSelected(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-zinc-700 h-8" onClick={() => nudge(-0.5, 0)}>
          ←
        </Button>
        <Button size="sm" variant="outline" className="border-zinc-700 h-8" onClick={() => nudge(0.5, 0)}>
          →
        </Button>
        <Button size="sm" variant="outline" className="border-zinc-700 h-8" onClick={() => nudge(0, -0.5)}>
          ↑
        </Button>
        <Button size="sm" variant="outline" className="border-zinc-700 h-8" onClick={() => nudge(0, 0.5)}>
          ↓
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-zinc-700 h-8"
          onClick={() => onChange({ ...DEFAULT_SLOTS_LAYOUT })}
        >
          Reset slots defaults
        </Button>
      </div>
      <div
        ref={boardRef}
        className="admin-layout-board relative mx-auto w-full max-w-md touch-none select-none overflow-hidden rounded-xl border border-amber-500/30 bg-black"
        style={{ aspectRatio: `${layout.artW} / ${layout.artH}` }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <img
          src={cabinetSrc}
          alt=""
          className="absolute inset-0 h-full w-full object-fill pointer-events-none"
          draggable={false}
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            if (!img.src.includes("cabinet-classic")) {
              img.src = "/slots/craftpix/cabinet-classic-sevens.jpg";
            }
          }}
        />
        {SLOT_REGIONS.map((r) => {
          const box = regionRect(layout, r.key);
          return (
            <div
              key={r.key}
              role="button"
              tabIndex={0}
              className={`absolute cursor-grab border-2 active:cursor-grabbing ${
                selected === r.key ? "z-10 shadow-[0_0_12px_currentColor]" : "opacity-70"
              }`}
              style={{
                left: `${box.left}%`,
                top: `${box.top}%`,
                width: `${box.width}%`,
                height: `${box.height}%`,
                borderColor: r.color,
                background: `${r.color}22`,
                color: r.color,
                borderRadius: r.key.includes("Circle") ? "50%" : 4,
              }}
              onPointerDown={(e) => onPointerDown(r.key, e)}
            >
              <span className="absolute left-0.5 top-0.5 rounded bg-black/70 px-1 text-[9px] font-bold uppercase">
                {r.label}
              </span>
            </div>
          );
        })}
      </div>
      <pre className="max-h-40 overflow-auto rounded-lg bg-black/50 p-2 text-[10px] text-zinc-500">
        {JSON.stringify(layout, null, 2)}
      </pre>
    </div>
  );
}

export function TableLayoutEditor({
  layoutKey,
  layout,
  onChange,
}: {
  layoutKey: string;
  layout: TableLayoutV1;
  onChange: (l: TableLayoutV1) => void;
}) {
  const boardRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    index: number;
    x: number;
    y: number;
    seats: TableLayoutV1["seats"];
  } | null>(null);

  const onPointerDown = (index: number, e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = {
      index,
      x: e.clientX,
      y: e.clientY,
      seats: layout.seats.map((s) => ({ ...s })),
    };
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current || !boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    const dLeft = ((e.clientX - drag.current.x) / rect.width) * 100;
    const dTop = ((e.clientY - drag.current.y) / rect.height) * 100;
    const seats = drag.current.seats.map((s, i) =>
      i === drag.current!.index
        ? {
            ...s,
            left: Math.min(98, Math.max(2, s.left + dLeft)),
            top: Math.min(98, Math.max(2, s.top + dTop)),
          }
        : s,
    );
    onChange(sanitizeTableLayout({ ...layout, seats }, layoutKey));
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-400">
        Drag seat nodes on the felt. Key: <code className="text-amber-200">{layoutKey}</code> ·{" "}
        {layout.maxPlayers}-max · {layout.vertical ? "portrait" : "landscape"}
      </p>
      <div
        ref={boardRef}
        className="admin-layout-board relative mx-auto w-full max-w-lg touch-none select-none overflow-hidden rounded-xl border border-emerald-500/30"
        style={{
          aspectRatio: layout.vertical ? "9 / 16" : "16 / 10",
          background:
            "radial-gradient(ellipse 70% 55% at 50% 48%, #0d4a2e 0%, #062416 55%, #021208 100%)",
        }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        {/* Felt oval */}
        <div
          className="absolute left-[8%] right-[8%] top-[12%] bottom-[12%] rounded-[50%] border-2 border-amber-700/40"
          style={{
            boxShadow: "inset 0 0 40px rgba(0,0,0,0.45)",
            background:
              "radial-gradient(ellipse at 50% 50%, #0f5c38 0%, #083822 70%)",
          }}
        />
        <div className="absolute left-1/2 top-1/2 h-10 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-black/20 text-center text-[10px] leading-10 text-white/40">
          POT
        </div>
        {layout.seats.map((s, i) => (
          <div
            key={`${s.label}-${i}`}
            role="button"
            tabIndex={0}
            className="absolute z-10 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 cursor-grab flex-col items-center justify-center rounded-full border-2 border-amber-300 bg-zinc-900/90 text-[9px] font-bold text-amber-100 shadow-lg active:cursor-grabbing"
            style={{ left: `${s.left}%`, top: `${s.top}%` }}
            onPointerDown={(e) => onPointerDown(i, e)}
            title={s.label}
          >
            {i + 1}
            <span className="max-w-[4.5rem] truncate text-[8px] font-normal text-zinc-400">
              {s.label}
            </span>
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-zinc-700"
        onClick={() =>
          onChange(
            sanitizeTableLayout(
              DEFAULT_TABLE_LAYOUTS[layoutKey] || layout,
              layoutKey,
            ),
          )
        }
      >
        Reset this table preset
      </Button>
    </div>
  );
}

export function LayoutEditorsPanel({
  bundle,
  onBundleChange,
  onPublish,
  publishing,
}: {
  bundle: LayoutBundle;
  onBundleChange: (b: LayoutBundle) => void;
  onPublish: () => void;
  publishing?: boolean;
}) {
  const [tab, setTab] = useState<"slots" | "table">("slots");
  const tableKeys = useMemo(
    () => Object.keys(bundle.tables).sort(),
    [bundle.tables],
  );
  const [tableKey, setTableKey] = useState("table-6");

  const applyDraft = useCallback(() => {
    writeDraftBundle(bundle);
  }, [bundle]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={tab === "slots" ? "default" : "outline"}
          className={tab !== "slots" ? "border-zinc-700" : ""}
          onClick={() => setTab("slots")}
        >
          Slots 2D
        </Button>
        <Button
          size="sm"
          variant={tab === "table" ? "default" : "outline"}
          className={tab !== "table" ? "border-zinc-700" : ""}
          onClick={() => setTab("table")}
        >
          Poker table 2D
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-zinc-700"
          onClick={applyDraft}
        >
          Apply draft (this browser)
        </Button>
        <Button size="sm" disabled={publishing} onClick={onPublish}>
          {publishing ? "Publishing…" : "Publish to production"}
        </Button>
        <a
          href="/slots"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-cyan-400 underline"
        >
          Open /slots
        </a>
        <a
          href="/lobby"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-cyan-400 underline"
        >
          Open /lobby
        </a>
      </div>

      {tab === "slots" ? (
        <SlotsLayoutEditor
          layout={bundle.slots}
          onChange={(slots) => onBundleChange({ ...bundle, slots })}
        />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1">
            {tableKeys.map((k) => (
              <button
                key={k}
                type="button"
                className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                  tableKey === k
                    ? "border-emerald-400 text-emerald-200"
                    : "border-zinc-700 text-zinc-400"
                }`}
                onClick={() => setTableKey(k)}
              >
                {k}
              </button>
            ))}
          </div>
          <TableLayoutEditor
            layoutKey={tableKey}
            layout={
              bundle.tables[tableKey] ||
              DEFAULT_TABLE_LAYOUTS[tableKey] ||
              DEFAULT_TABLE_LAYOUTS["table-6"]!
            }
            onChange={(t) =>
              onBundleChange({
                ...bundle,
                tables: { ...bundle.tables, [tableKey]: t },
              })
            }
          />
        </div>
      )}
    </div>
  );
}
